import PropTypes from 'prop-types'
import { Input } from 'baseui/input'
import { mergeOverrides, useStyletron } from 'baseui'
import { SDInputOverrides, SDInputPulseOverrides } from './styled-components'
import { Alert, Delete } from 'baseui/icon'
import IconCarbonCheckmarkOutline from '@/shared/icons/IconCarbonCheckmarkOutline'
import InfoIcon from '../icon_info.svg'
import { Fragment, useEffect, useState } from 'react'
import { EndEnhancerIconContainer, EnhancerRow, RelativeContainer } from '@/shared/theme/styled-components'
import { Spinner } from 'baseui/spinner'
import usePulseScope from '@/shared/components/PulseScope/usePulseScope'
import SDFieldLabel from '../SDFieldLabel/SDFieldLabel'
import { PulseFieldContainer, PulseFieldRow } from '../SDFieldLabel/styled-components'

const SDInput = ({
  label,
  overrides,
  status,
  currentValue,
  onBlur,
  outerOnchange,
  error,
  missing = false,
  button = null,
  ...rest
}) => {
  const [value, setValue] = useState(currentValue || '')
  const isPulse = usePulseScope()
  const [, theme] = useStyletron()
  const iconSize = isPulse ? 16 : 24

  const iconMapping = {
    valid: <IconCarbonCheckmarkOutline size={iconSize} color={isPulse ? theme.colors.successPulse : 'green'} />,
    info: <img src={InfoIcon} alt='Info' style={{ width: iconSize, height: iconSize }} />,
    warning: <Alert size={iconSize} color='orange' />,
    error: <Delete size={iconSize} color={isPulse ? theme.colors.dangerPulse : 'red'} />,
    loading: <Spinner size={iconSize} />,
  }

  const iconEndEnhancer = iconMapping[status] ?? null

  const resolveEnhancerContent = (icon, enhancer) => {
    if (!icon) return enhancer || null
    if (!enhancer) return icon
    return <EnhancerRow>{icon}{enhancer}</EnhancerRow>
  }

  const endEnhancerContent = resolveEnhancerContent(iconEndEnhancer, rest.endEnhancer)
  const endEnhancerElement = endEnhancerContent ? (
    <EndEnhancerIconContainer>{endEnhancerContent}</EndEnhancerIconContainer>
  ) : null

  const baseOverrides = isPulse ? SDInputPulseOverrides(error || false, missing) : SDInputOverrides(error || false, !!label)
  const newOverrides = mergeOverrides(baseOverrides, overrides)
  const Container = isPulse ? PulseFieldContainer : RelativeContainer
  const ControlRow = isPulse && button ? PulseFieldRow : Fragment

  useEffect(() => {
    if ((currentValue === null || currentValue === undefined) && value !== '') {
      setValue('')
      return
    }
    setValue(currentValue)
  }, [currentValue])

  return (
    <Container>
      {label && <SDFieldLabel label={label} />}
      <ControlRow>
        <Input
          {...rest}
          error={error}
          overrides={newOverrides}
          onBlur={e => {
            if (typeof onBlur === 'function') {
              let selectedValue = e.target.value
              if (selectedValue === '') {
                selectedValue = null
              }
              onBlur(selectedValue)
            }
          }}
          onChange={e => {
            const newValue = e.target.value
            setValue(newValue)
            if (typeof outerOnchange === 'function') {
              let selectedValue = newValue
              if (selectedValue === '') {
                selectedValue = null
              }
              outerOnchange(selectedValue)
            }
          }}
          endEnhancer={
            button && !isPulse
              ? () => (
                  <>
                    {endEnhancerElement}
                    {button}
                  </>
                )
              : endEnhancerElement
          }
          value={value || ''}
        />
        {isPulse && button}
      </ControlRow>
    </Container>
  )
}

SDInput.propTypes = {
  label: PropTypes.node,
  overrides: PropTypes.object,
  status: PropTypes.oneOf(['valid', 'info', 'warning', 'error']),
  currentValue: PropTypes.any,
  onBlur: PropTypes.func,
  onChange: PropTypes.func,
  error: PropTypes.oneOfType([PropTypes.string, PropTypes.bool]),
  missing: PropTypes.bool,
}

export default SDInput
